import { call } from 'redux-saga/effects'
import qs from 'query-string'
import { authService } from 'src/services/AuthService'
import _ from 'lodash'
import { logger } from 'src/utils/logger'

const getBaseUrl = (apiService: ApiService) => {
  if (apiService === ApiService.Paraworks) {
    return process.env.REACT_APP_API_URL
  }

  return process.env.REACT_APP_API_URL
}

export interface Options {
  headers?: {
    [x: string]: string
  }
  method?: string
  body?: any
  query?: string | Record<string, Array<string> | string | number | undefined>
  returnResponse?: boolean
  rewriteUrl?: boolean
  apiService?: ApiService
  params?: Array<UrlReplaceParams>
  resetContentType?: boolean
  returnBuffer?: boolean
}

export interface UrlReplaceParams {
  field: string
  value: string
}

export enum ApiService {
  Paraworks = 'paraworks',
}

interface ApiErrorPayload {
  error?: string
  message?: string
  statusCode?: number
}

export interface CallApiResponse<P = any> {
  status: number
  ok: boolean
  payload: P
}

export const callApi = async (
  endpoint: string,
  options: Options = {},
): Promise<CallApiResponse> => {
  const {
    headers,
    method = 'GET',
    body,
    query,
    returnResponse,
    rewriteUrl,
    apiService = ApiService.Paraworks,
    params = [],
    resetContentType,
    returnBuffer,
  } = options
  let requestHeaders: { [x: string]: string } = {}
  let requestBody
  const url = rewriteUrl ? '' : getBaseUrl(apiService)

  if (!resetContentType) {
    requestHeaders = {
      ...requestHeaders,
      'Content-Type': 'application/json',
    }
  }

  if (headers) {
    requestHeaders = { ...requestHeaders, ...headers }
  }

  if (body) {
    if (resetContentType) {
      requestBody = body
    } else {
      requestBody = JSON.stringify(body)
    }
  }

  const endpointWithParams = params.reduce(
    (acc, param) => acc.replace(param.field, param.value),
    endpoint,
  )

  let endpointWithQuery = endpointWithParams

  if (query) {
    if (typeof query === 'object') {
      endpointWithQuery += `?${qs.stringify(_.omitBy(query, _.isNil), { encode: false })}`
    } else if (typeof query === 'string') {
      endpointWithQuery += `?${query}`
    }
  }

  const response = await fetch(url + endpointWithQuery, {
    method,
    headers: requestHeaders,
    body: requestBody,
  })

  const isOk = response.status >= 200 && response.status < 400

  const responseObject: { status: number; ok: boolean; payload: any } = {
    status: response.status,
    ok: isOk,
    payload: null,
  }

  if (isOk && returnBuffer) {
    responseObject.payload = await response.arrayBuffer()
  } else if (isOk && returnResponse) {
    responseObject.payload = await response.text()
  } else {
    // A controller returning `null` (e.g. due-interest with nothing due) answers 200 with an EMPTY
    // body, which `response.json()` rejects. An empty body carries no value, so it becomes `null`;
    // malformed JSON still throws.
    const text = await response.text()

    responseObject.payload = text.trim() === '' ? null : JSON.parse(text)
  }

  if (!responseObject.ok) {
    logger.error(`API Error: ${method} ${endpoint} (${responseObject.status})`, null, {
      status: responseObject.status,
      endpoint,
      method,
      payload: responseObject.payload,
    })

    throw responseObject
  }

  return responseObject
}

export function* handleApiError(error: CallApiResponse<ApiErrorPayload>) {
  // Nothing is rewritten or cleared here any more. Under internal RBAC a 403 is a real
  // per-permission answer from the API — the caller's saga surfaces the backend's own message,
  // and AuthKit owns the session itself, so a failed request is never a reason to end one.
  throw error
}

// @ts-ignore
export function* callSecureApi(endpoint: string, options: Options = {}) {
  // AuthKit refreshes the token on its own schedule and throws once the session is truly gone,
  // so there is no refresh flow of ours to run here: no token means no session.
  const authToken: string | null = yield call([authService, authService.getAccessToken])

  if (!authToken) {
    throw new Error('Unauthorized')
  }

  const serviceHeaders: { [x: string]: string } = {}

  const optionsWithCredentials = {
    ...options,
    headers: {
      ...options?.headers,
      ...serviceHeaders,
      Authorization: `Bearer ${authToken}`,
    },
  }

  try {
    // @ts-ignore
    const res = yield call(callApi, endpoint, optionsWithCredentials)

    return res
  } catch (error: any) {
    yield call(handleApiError, error)
  }
}
